/* eslint-disable max-len */
import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import EnvironmentSlider from '/@/components/environment-slider';
import DateInput from '/@/components/forms/date-input';
import HumidityMapModal from '/@/components/humidity-map-modal';
import Message from '/@/components/message';
import Title from '/@/components/title';
import settings from '/@/core/settings';
import { useHumidityMaps } from '/@/hooks/maps';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _, _lazy } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getSessionCountry } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import styles from './styles.module.css';

const CropConfiguration = ({
    recommendation,
    cropConfig,
    environment,
    soilTypeChoices,
    irrigationChoices,
    validPlantingDates,
    showPlantingDateWarning,
    soilMoistureChoices,
    climatePatternChoices,
    setPlantingDate,
    setClimatePattern,
    setSoilMoisture,
    setSoilType,
    setIrrigation,
    setEnvironment,
    field,
    loading,
}) => {
    const { crop, plantingDate, soilMoisture, climatePattern, soilType, irrigation } = cropConfig;
    const sessionCountry = useSelector(getSessionCountry);
    const { toggle: toggleHumidityMapModal, open: humidityMapModalOpened } = useToggle(false);
    const [mapVersion, setMapVersion] = useState();
    const [humidityMaps, setHumidityMaps] = useState([]);
    const { data: maps, isValidating: isLoading } = useHumidityMaps({ country: sessionCountry.code });

    useEffect(() => {
        if (!isLoading && maps) {
            const filteredMaps = maps.find((m) => m.type === 'humidity');
            setHumidityMaps(filteredMaps);
        }
    }, [maps]);

    useEffect(() => {
        if (humidityMaps && humidityMaps.versions) {
            setMapVersion(humidityMaps.versions[0]);
        }
    }, [humidityMaps]);

    const parseOverlay = (mapV) => {
        return {
            version: mapV,
            overlay: humidityMaps,
        };
    };

    const getEnvironmentHelpText = () => {
        if (environment.configurable)
            return _(
                'Utilizando modelos de simulación te presentamos la situación más probable para tu lote en función de las siguientes variables que podés configurar. Deslizá el círculo para seleccionar tu ambiente.'
            );
        if (environment.percentiles.length)
            return _(
                'Utilizando datos históricos te presentamos la situación promedio para tu lote. Deslizá el círculo para seleccionar tu ambiente.'
            );
        return _('Deslizá el círculo para seleccionar tu ambiente.');
    };

    const handleOverlayChange = (overlay) => {
        if (!overlay) return;
        if (typeof overlay.version === 'number') setMapVersion(humidityMaps.versions[overlay.version]);
        else {
            setHumidityMapModalOpened;
        }
    };

    return (
        <Box sx={{ py: 4 }}>
            <div className={styles.section}>
                <Title>{_('Fecha de siembra')}</Title>
                <div className={styles.row}>
                    <DateInput
                        placeholder={_('Fecha de siembra')}
                        value={plantingDate}
                        validDates={validPlantingDates}
                        onChange={setPlantingDate}
                        disabled={loading || recommendation.status === 'read_only'}
                        app={settings.app.theme}
                        showTooltip
                        tooltipMessage={_(
                            'No podemos ofrecer recomendaciones para esta fecha porque aún no se han actualizado los datos de los híbridos para la nueva campaña'
                        )}
                    />
                    <div />
                    <div />
                </div>
                {showPlantingDateWarning && (
                    <div className={styles.message}>
                        <Message type='warning' appearance='outline' showIcon>
                            {_(
                                'Recomendamos ajustar la fecha de siembra para una más favorable según muestra el cuadro, dado que no hay productos recomendados en situación óptima o buena para la fecha seleccionada.'
                            )}
                        </Message>
                    </div>
                )}
            </div>
            <br />
            <div className={styles.section}>
                <Title>{_('Ambiente')}</Title>
                <p>{getEnvironmentHelpText()}</p>
                {environment.configurable && (
                    <Grid container spacing={isMobile() ? 2 : 4} direction={isMobile() ? 'column' : 'row'}>
                        {crop === 'corn' && (
                            <Grid item xs={12} md={4}>
                                <MuiSelect
                                    inputLabelProps={{ sx: { color: 'black' } }}
                                    formControlProps={{ fullWidth: true, variant: 'filled' }}
                                    inputLabel={_('Año climático')}
                                    value={climatePattern}
                                    width='100%'
                                    onChange={(e) => setClimatePattern(e.target.value)}
                                    options={climatePatternChoices}
                                    disabled={loading || recommendation.status === 'read_only'}
                                    data-testid='recommendation-edit-climate-pattern-select'
                                />
                            </Grid>
                        )}
                        {soilMoistureChoices && soilMoistureChoices.length > 0 && (
                            <Grid item xs={12} md={4}>
                                <Box display='flex'>
                                    <Box width='100%'>
                                        <MuiSelect
                                            inputLabelProps={{ sx: { color: 'black' } }}
                                            formControlProps={{ fullWidth: true, variant: 'filled' }}
                                            inputLabel={_('Agua en el suelo')}
                                            value={soilMoisture}
                                            width='100%'
                                            onChange={(e) => setSoilMoisture(e.target.value)}
                                            options={soilMoistureChoices}
                                            disabled={loading || recommendation.status === 'read_only'}
                                            data-testid='recommendation-edit-soil-moisture-select'
                                        />
                                    </Box>
                                    {field.country.code === 'AR' && (
                                        <IconButton
                                            size='large'
                                            sx={{ color: (t) => t.palette.primary.main }}
                                            onClick={toggleHumidityMapModal}
                                        >
                                            <InfoIcon fontSize='inherit' />
                                        </IconButton>
                                    )}
                                </Box>
                            </Grid>
                        )}
                        {soilTypeChoices && soilTypeChoices.filter((el) => el !== null).length > 0 && (
                            <Grid item xs={12} md={4}>
                                <MuiSelect
                                    inputLabelProps={{ sx: { color: 'black' } }}
                                    formControlProps={{ fullWidth: true, variant: 'filled' }}
                                    inputLabel={_('Suelo')}
                                    value={soilType}
                                    width='100%'
                                    onChange={(e) => setSoilType(e.target.value)}
                                    options={soilTypeChoices.filter((el) => el !== null)}
                                    disabled={loading || recommendation.status === 'read_only'}
                                    clearable={soilTypeChoices.some((el) => el === null)}
                                    data-testid='recommendation-edit-soil-type-select'
                                />
                            </Grid>
                        )}
                        {irrigationChoices && irrigationChoices.filter((el) => el !== null).length > 1 && (
                            <Grid item xs={12} md={4}>
                                <MuiSelect
                                    inputLabelProps={{ sx: { color: 'black' } }}
                                    formControlProps={{ fullWidth: true, variant: 'filled' }}
                                    inputLabel={_('Irrigado')}
                                    value={irrigation}
                                    width='100%'
                                    onChange={(e) => setIrrigation(e.target.value)}
                                    options={irrigationChoices}
                                    disabled={loading || recommendation.status === 'read_only'}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
                <EnvironmentSlider
                    crop={crop}
                    min={environment.min}
                    max={environment.max}
                    environment={cropConfig.environment}
                    percentiles={environment.percentiles}
                    plantingDate={plantingDate}
                    onAfterChange={setEnvironment}
                    disabled={loading || recommendation.status === 'read_only'}
                    dataTestId='recommendation-edit-environment-slider'
                />
            </div>

            <HumidityMapModal
                open={humidityMapModalOpened}
                toggleHumidityMapModal={toggleHumidityMapModal}
                field={field}
                mapVersion={parseOverlay(mapVersion)}
                handleOverlayChange={handleOverlayChange}
            />
        </Box>
    );
};

CropConfiguration.menuLabel = _lazy('Fecha de siembra y ambiente');
export default CropConfiguration;
